/**
 * Разбор query-string initData без URLSearchParams: в WKWebView (Telegram iOS)
 * иногда new URLSearchParams(longString) бросает «The string did not match the expected pattern».
 */
function parseInitDataKeyValues(initData: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!initData || typeof initData !== 'string') {
    return out;
  }
  const parts = initData.split('&');
  for (const part of parts) {
    if (!part) continue;
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    try {
      const key = decodeURIComponent(part.slice(0, eq));
      const rawVal = part.slice(eq + 1);
      const value = decodeURIComponent(rawVal.replace(/\+/g, ' '));
      out[key] = value;
    } catch {
      /* пропускаем битый фрагмент */
    }
  }
  return out;
}

/**
 * Подождать появления user в WebApp.
 * С кнопки reply keyboard initData / initDataUnsafe иногда приходят с задержкой (особенно iOS).
 */
export async function waitForTelegramUser(
  maxAttempts = 90,
  delayMs = 120
): Promise<any | null> {
  // @ts-ignore
  const tg = window.Telegram?.WebApp;

  const tryUser = () => {
    try {
      return getTelegramUser();
    } catch (e) {
      console.warn('getTelegramUser:', e);
      return null;
    }
  };

  let hit = tryUser();
  if (hit?.id) return hit;

  return await new Promise((resolve) => {
    let attempts = 0;
    let settled = false;

    const finish = (u: any | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(u);
    };

    const tick = () => {
      if (settled) return;
      attempts++;
      const u = tryUser();
      if (u?.id) {
        finish(u);
        return;
      }
      if (attempts >= maxAttempts) {
        finish(null);
        return;
      }
      if (tg && attempts % 12 === 0) {
        try {
          tg.ready();
          tg.expand();
        } catch {
          /* ignore */
        }
      }
      window.setTimeout(tick, delayMs);
    };

    const onVp = () => {
      const u = tryUser();
      if (u?.id) finish(u);
    };

    function cleanup() {
      if (tg && typeof tg.offEvent === 'function') {
        try {
          tg.offEvent('viewportChanged', onVp);
        } catch {
          /* ignore */
        }
      }
    }

    if (tg && typeof tg.onEvent === 'function') {
      try {
        tg.onEvent('viewportChanged', onVp);
      } catch {
        /* ignore */
      }
    }

    tick();
  });
}

const PROFILE_START_STORAGE_KEY = 'fcyx_tg_profile_start';

// Инициализация Telegram Mini App
export const initTelegram = () => {
    // @ts-ignore
    const tg = window.Telegram?.WebApp;
    
    if (tg) {
        try {
          tg.ready();
        } catch (e) {
          console.warn('tg.ready:', e);
        }
        try {
          tg.expand();
        } catch (e) {
          console.warn('tg.expand:', e);
        }

        console.log('Telegram Web App инициализирован');
        console.log('Пользователь из Telegram:', tg.initDataUnsafe?.user);

        persistProfileStartParamFromTelegram();
        
        return tg;
    } else {
        console.log('Запущено не в Telegram');
        return null;
    }
};

/** Числовой id профиля из пути /u/123 (ссылка из канала). */
export function getProfileUserIdFromPathname(): number | null {
  try {
    const m = /^\/u\/(\d+)\/?$/i.exec(window.location.pathname || '');
    if (m) {
      const n = parseInt(m[1], 10);
      if (!Number.isNaN(n)) return n;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Id профиля, записанный в index.html из Telegram start_param до загрузки бандла. */
export function getStoredPendingProfileUserId(): number | null {
  try {
    const s = sessionStorage.getItem(PROFILE_START_STORAGE_KEY);
    if (s == null) return null;
    const m = /^profile_(\d+)$/.exec(String(s).trim());
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return Number.isNaN(n) ? null : n;
  } catch {
    return null;
  }
}

/** start_param для Web App (прямая ссылка t.me/bot/shortname?startapp=profile_123). */
export function getTelegramStartParam(): string | null {
  /** Путь из канала — самый стабильный способ передать id после открытия Mini App. */
  try {
    const pathId = getProfileUserIdFromPathname();
    if (pathId != null) return `profile_${pathId}`;
  } catch {
    /* ignore */
  }

  /** Query / hash — запасные варианты. */
  try {
    const p = new URLSearchParams(window.location.search);
    const tsp = p.get('tgWebAppStartParam');
    if (tsp != null && String(tsp).trim()) return String(tsp).trim();
    const fc = p.get('fc_profile');
    if (fc != null && /^\d+$/.test(String(fc).trim())) return `profile_${String(fc).trim()}`;
  } catch {
    /* ignore */
  }

  try {
    const hp = window.location.hash.replace(/^#/, '').trim();
    const hm = /^profile\/(\d+)$/.exec(hp);
    if (hm) return `profile_${hm[1]}`;
  } catch {
    /* ignore */
  }

  try {
    const h = window.location.hash.replace(/^#/, '').trim();
    if (h) {
      const queryPart = h.includes('?') ? h.slice(h.indexOf('?') + 1) : h;
      const qFromHash = parseInitDataKeyValues(queryPart);
      const hf =
        qFromHash.tgWebAppStartParam ||
        qFromHash.start_param ||
        qFromHash.startapp;
      if (hf != null && String(hf).trim()) return String(hf).trim();
    }
  } catch {
    /* ignore */
  }

  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (isLocalhost) {
    const search = window.location.search.startsWith('?')
      ? window.location.search.slice(1)
      : window.location.search;
    const q = parseInitDataKeyValues(search);
    const qs = q.startapp || q.start_param;
    if (qs != null && String(qs).trim()) return String(qs).trim();
  }
  // @ts-ignore
  const tg = window.Telegram?.WebApp;
  if (!tg) return null;
  // Bot API 6.4+: то же, что start_param в initDataUnsafe
  // @ts-ignore
  const direct = tg.startParam;
  if (direct != null && String(direct).trim()) return String(direct).trim();
  const unsafe = tg.initDataUnsafe?.start_param;
  if (unsafe != null && String(unsafe).trim()) return String(unsafe).trim();
  if (tg.initData && typeof tg.initData === 'string') {
    const p = parseInitDataKeyValues(tg.initData);
    const v = p.start_param;
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return null;
}

/** Сохраняет profile_* из TG — startParam иногда появляется с задержкой после ready(). */
export function persistProfileStartParamFromTelegram(): void {
  try {
    const live = getTelegramStartParam();
    const t = live != null ? String(live).trim() : '';
    if (t && /^(profile|order)_\d+$/.test(t)) {
      sessionStorage.setItem(PROFILE_START_STORAGE_KEY, t);
    }
  } catch {
    /* ignore */
  }
}

export function getPendingProfileStartParam(): string | null {
  const live = getTelegramStartParam();
  if (live != null && /^(profile|order)_\d+$/.test(String(live).trim())) return String(live).trim();
  try {
    const s = sessionStorage.getItem(PROFILE_START_STORAGE_KEY);
    if (s != null && /^(profile|order)_\d+$/.test(String(s).trim())) return String(s).trim();
  } catch {
    /* ignore */
  }
  return null;
}

export function clearPendingProfileStartParam(): void {
  try {
    sessionStorage.removeItem(PROFILE_START_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Убрать из адресной строки параметры запуска из канала (чтобы не триггерить профиль при обновлении). */
export function stripProfileLaunchParamsFromLocation(): void {
  try {
    const u = new URL(window.location.href);
    let changed = false;
    if (/^\/u\/\d+\/?$/i.test(u.pathname)) {
      u.pathname = '/';
      changed = true;
    }
    if (u.searchParams.has('tgWebAppStartParam')) {
      u.searchParams.delete('tgWebAppStartParam');
      changed = true;
    }
    if (u.searchParams.has('fc_profile')) {
      u.searchParams.delete('fc_profile');
      changed = true;
    }
    if (u.hash && /^#profile\/\d+$/i.test(u.hash)) {
      u.hash = '';
      changed = true;
    }
    if (changed) {
      window.history.replaceState(null, '', u.pathname + u.search + u.hash);
    }
  } catch {
    /* ignore */
  }
}

// Получение данных пользователя из Telegram или из URL для тестирования
export const getTelegramUser = () => {
    console.log('🔥 telegram.ts загружен!');
    console.log('🔥 getTelegramUser START');
    console.log('window.location.href:', window.location.href);
    console.log('window.location.search:', window.location.search);
    
    // Сначала проверяем, есть ли реальный пользователь Telegram
    // @ts-ignore
    const tg = window.Telegram?.WebApp;
    
    if (tg?.initDataUnsafe?.user) {
        console.log('📱 Использую данные из Telegram:', tg.initDataUnsafe.user);
        return tg.initDataUnsafe.user;
    }

    // Фолбэк: иногда user нет в initDataUnsafe, но есть в initData
    if (tg?.initData && typeof tg.initData === 'string') {
        try {
            const params = parseInitDataKeyValues(tg.initData);
            const userRaw = params.user;
            if (userRaw) {
                const parsed = JSON.parse(userRaw);
                if (parsed?.id) {
                    console.log('📱 Использую пользователя из initData:', parsed);
                    return parsed;
                }
            }
        } catch (error) {
            console.log('❌ Не удалось распарсить пользователя из initData:', error);
        }
    }
    
    // Тестовый режим разрешён только локально
    const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (!isLocalhost) {
        console.log('⚠️ Прод-режим: Telegram user не найден, test_id игнорируется');
        return null;
    }

    // Для тестирования разных пользователей в локальном браузере через параметр test_id
    const search = window.location.search.startsWith('?')
      ? window.location.search.slice(1)
      : window.location.search;
    const queryParams = parseInitDataKeyValues(search);
    const testId = queryParams.test_id;
    
    // Добавляем проверку на наличие test_id в URL
    console.log('🔍 Параметр test_id из URL:', testId);
    
    if (testId) {
        const userId = parseInt(testId);
        console.log('🧪 Режим тестирования: test_id =', userId);
        
        // Проверяем, что userId - число
        if (isNaN(userId)) {
            console.log('❌ test_id не является числом');
            return {
                id: 123456789,
                first_name: 'Тест',
                last_name: 'Пользователь',
                username: 'testuser'
            };
        }
        
        // Для test_id=1 используем существующего пользователя
        if (userId === 1) {
            return {
                id: 123456789,  // существующий пользователь в базе
                first_name: 'Тест',
                last_name: 'Пользователь',
                username: 'testuser'
            };
        }
        
        // Для других test_id создаём новых пользователей
        return {
            id: userId,
            first_name: `Тест ${userId}`,
            last_name: 'Пользователь',
            username: `test${userId}`
        };
    }
    
    // Если данных Telegram нет и test_id не указан — пользователя определить нельзя
    console.log('⚠️ Локальный режим: нет Telegram user и test_id, возвращаю null');
    return null;
};